/* eslint-disable no-restricted-syntax */
/* eslint-disable class-methods-use-this */

'use strict';

/**
 * Extra time allowed past the fade animation before the fallback timer steps
 * in. The animation is the primary trigger; this only covers the case where it
 * never runs at all, for instance if the stylesheet failed to load.
 */
const FALLBACK_SLACK_MS = 500;

class ToastMessage {
    constructor(message = '', {
        duration = 3000,
        type = '',
        position = 'fixed',
        closable = false,
        closeLabel = 'Close',
        modifier = ''
    } = {}) {
        this.duration = duration;
        this.type = type;
        this.message = message;
        this.position = position;
        this.closable = closable;
        this.closeLabel = closeLabel;
        this.modifier = modifier;
        this.onAnimationEnd = this.onAnimationEnd.bind(this);
        this.onCloseClick = this.onCloseClick.bind(this);
        this.onInteractive = this.onInteractive.bind(this);
        this.render();
    }

    get closeButtonTemplate() {
        if (!this.closable) {
            return '';
        }
        return `
                <button type="button" class="g-toast-message-close" data-elem="close" aria-label="${this.closeLabel}"></button>
        `;
    }

    get template() {
        // `role="status"` with `aria-live="polite"` makes the message audible to
        // a screen reader without stealing focus, which a save confirmation
        // must not do.
        return `
            <div class="g-toast-message ${this.type} ${this.position} ${this.modifier}" style="--value:${this.duration / 1000}s" role="status" aria-live="polite">
                <div class="g-toast-message-body" data-elem="body">
                    ${this.message}
                </div>${this.closeButtonTemplate}
            </div>
        `;
    }

    render() {
        const element = document.createElement('div');
        element.innerHTML = this.template;
        this.element = element.firstElementChild;
        this.subElements = this.getSubElements(this.element);
        // Lets a later message dismiss this one through the instance that owns
        // it instead of deleting its node behind its back. See `show`.
        //
        // A property on the node rather than jQuery's `data()` store: `remove`
        // detaches the element with the native `Element.remove()`, which does
        // not clear jQuery's cache, so every message shown would be retained
        // there for the life of the page. An expando is collected with its
        // node.
        this.element.toastInstance = this;
    }

    show(parent = document.body) {
        const $notification = $('.g-toast-message');
        const owner = $notification.length ? $notification[0].toastInstance : null;

        // Deleting the node on its own strands whatever the previous message's
        // caller hung on dismissal: the wishlist save confirmation removes its
        // backdrop from `remove()`, and a message shown over it used to leave
        // that scrim covering the page until the orphaned instance's fallback
        // timer eventually fired. Asking the owner to remove itself runs its
        // teardown, clears its timer and drops its listeners. The node leaves
        // the document either way, so every other caller is unaffected.
        if (owner && owner !== this && typeof owner.remove === 'function') {
            owner.remove();
        }

        if ($notification.length) {
            $notification.remove();
        }

        parent.append(this.element);

        // Removal is driven by the end of the fade animation rather than a
        // fixed timer, so that pausing the animation on hover or keyboard
        // focus genuinely holds the message open instead of only freezing its
        // opacity while a timer keeps running underneath.
        this.element.addEventListener('animationend', this.onAnimationEnd);

        if (this.subElements.close) {
            this.subElements.close.addEventListener('click', this.onCloseClick);
        }

        this.armFallbackTimer();
        this.armInteractiveTimer();
    }

    onAnimationEnd(event) {
        if (event.animationName && event.animationName.indexOf('fade') === -1) {
            return;
        }
        this.remove();
    }

    onCloseClick() {
        this.remove();
    }

    /**
     * True while the customer is hovering the message or has keyboard focus
     * inside it. The stylesheet pauses the animation in the same conditions.
     *
     * @returns {boolean} whether auto-hide is currently suspended
     */
    isPaused() {
        if (!this.element) {
            return false;
        }
        const hovered = typeof this.element.matches === 'function' && this.element.matches(':hover');
        const focused = document.activeElement && this.element.contains(document.activeElement);
        return Boolean(hovered || focused);
    }

    armFallbackTimer() {
        clearTimeout(this.timerId);
        this.timerId = setTimeout(() => {
            if (this.isPaused()) {
                this.armFallbackTimer();
                return;
            }
            this.remove();
        }, this.duration + FALLBACK_SLACK_MS);
    }

    /**
     * Enables the CSS hover/focus pause only after the fade-in completes.
     *
     * The animation's first 10% is the opacity 0 → 1 transition. If the
     * cursor is already over the toast when it appears — the heart icon the
     * customer just clicked and has not moved away from — pausing during that
     * window freezes the panel at opacity 0, invisible. Adding `m-interactive`
     * after the window closes lets the fade-in run regardless of hover, so the
     * panel is always seen before the pause can hold it (SFCC-7428).
     *
     * @returns {void}
     */
    armInteractiveTimer() {
        clearTimeout(this.interactiveTimerId);
        this.interactiveTimerId = setTimeout(this.onInteractive, Math.ceil(this.duration * 0.1) + 100);
    }

    onInteractive() {
        if (this.element) {
            this.element.classList.add('m-interactive');
        }
    }

    getSubElements(element) {
        const result = {};
        const elements = element.querySelectorAll('[data-elem]');
        for (const subElement of elements) {
            const name = subElement.dataset.elem;
            result[name] = subElement;
        }
        return result;
    }

    destroy() {
        this.remove();
        this.element = null;
        this.subElements = {};
    }

    remove() {
        clearTimeout(this.timerId);
        clearTimeout(this.interactiveTimerId);

        if (this.element) {
            this.element.removeEventListener('animationend', this.onAnimationEnd);
            if (this.subElements && this.subElements.close) {
                this.subElements.close.removeEventListener('click', this.onCloseClick);
            }
            this.element.remove();
        }
    }
}

module.exports = ToastMessage;
